import { Link } from 'react-router-dom';
import { getPublished } from '@/data/loader';
import { useI18n, useLocalizedPath } from '@/hooks/useI18n';
import { PasseiosBarcoSEO } from '@/components/SEO';
import { Container } from '@/components/Container';
import { OptimizedImage } from '@/components/OptimizedImage';
import { AuthorBox } from '@/components/AuthorBox';
import { defaultAuthor } from '@/data/author';
import pilar from '@/data/passeios-barco-pilar.json';

// Ordem editorial dos náuticos na pilar (do carro-chefe ao utilitário)
const ORDEM = [
  'passeio-baia-dos-golfinhos',
  'passeio-360',
  'aluguel-lancha-exclusiva',
  'lancha-por-do-sol-ponta-oeste',
  'passeio-jet-ski',
  'taxi-nautico-ilha-do-mel',
];
const PRA_QUEM: Record<string, string> = {
  'passeio-baia-dos-golfinhos': 'Primeira vez, famílias, ver os botos',
  'passeio-360': 'Ver a ilha inteira num só passeio',
  'aluguel-lancha-exclusiva': 'Grupos que querem roteiro próprio',
  'lancha-por-do-sol-ponta-oeste': 'Casais, fim de dia contemplativo',
  'passeio-jet-ski': 'Adrenalina, pouco tempo',
  'taxi-nautico-ilha-do-mel': 'Logística entre vilas e continente',
};

function precoLabel(p: any): string {
  if (p?.price?.amount) {
    const v = p.price.amount;
    return `R$${v % 1 === 0 ? v : v.toFixed(2).replace('.', ',')}`;
  }
  return 'Sob consulta';
}

export function PasseiosBarcoPage() {
  const { lang } = useI18n();
  const localize = useLocalizedPath();
  const passeios = getPublished('passeio', lang);
  const nauticos = ORDEM.map(h => passeios.find((p: any) => p.handle === h)).filter(Boolean) as any[];
  const faq = pilar.faq as { q: string; a: string }[];

  return (
    <>
      <PasseiosBarcoSEO nauticos={nauticos} />
      <div>
        {/* Hero */}
        <div className="relative mb-10">
          <div className="relative h-56 sm:h-72">
            <OptimizedImage
              src="/images/sailboat.jpg"
              alt="Passeio de barco contornando a Ilha do Mel"
              className="w-full h-full object-cover"
              priority
            />
            <div className="absolute inset-0 hero-gradient" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center text-center px-4">
            <div>
              <h1 className="font-display text-5xl sm:text-6xl text-white mb-2">
                Passeios de Barco na Ilha do Mel
              </h1>
              <p className="font-editorial text-xl text-white/80">
                Golfinhos, volta na ilha, pôr do sol e lancha exclusiva — rotas, preços e como reservar
              </p>
            </div>
          </div>
        </div>

        <Container className="pb-20">
          {/* Resposta direta (featured snippet) + intro */}
          <div className="max-w-3xl mx-auto mb-12 space-y-4">
            <p className="font-editorial text-lg text-warm-gray leading-relaxed">
              O passeio de barco é o programa mais pedido da{' '}
              <Link to="/" className="text-teal-deep font-medium hover:underline">Ilha do Mel</Link>: sai a
              partir de <strong>R$80 por pessoa</strong>, dura de 1h30 a 5 horas conforme a rota e mostra o que
              não se vê por terra — a Gruta pelo mar, a Fortaleza, a Baía dos Golfinhos e a Ponta Oeste.
            </p>
            <p className="font-body text-warm-gray leading-relaxed">
              Aqui você compara as rotas lado a lado, entende o que cada uma inclui (e o que fica de fora, como
              o almoço na Ilha das Peças) e vê como reservar — no trapiche, por WhatsApp ou online. Os valores
              são "a partir de" e variam por operador e temporada.
            </p>
          </div>

          {/* Tabela comparativa */}
          <section className="mb-16" id="comparativo">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">As rotas lado a lado</h2>
            <p className="font-editorial text-warm-gray mb-6">
              Preço de partida, duração e o perfil de cada passeio náutico.
            </p>
            <div className="overflow-x-auto rounded-2xl border border-sand-200">
              <table className="w-full text-left border-collapse min-w-[720px]">
                <thead>
                  <tr className="bg-sand-50 text-warm-dark">
                    <th className="font-display text-sm p-3">Passeio</th>
                    <th className="font-display text-sm p-3">Duração</th>
                    <th className="font-display text-sm p-3">A partir de</th>
                    <th className="font-display text-sm p-3">Sai de</th>
                    <th className="font-display text-sm p-3">Pra quem</th>
                  </tr>
                </thead>
                <tbody>
                  {nauticos.map((p: any, i: number) => (
                    <tr key={p.handle} className={`border-t border-sand-100 ${i % 2 ? 'bg-sand-50/40' : 'bg-white'}`}>
                      <td className="p-3 font-body text-sm font-medium text-warm-dark">
                        <Link to={localize(`/passeio/${p.handle}`)} className="hover:text-teal-deep hover:underline">
                          {p.title}
                        </Link>
                      </td>
                      <td className="p-3 font-body text-sm text-warm-gray">{p.duration}</td>
                      <td className="p-3 font-body text-sm text-warm-gray">{precoLabel(p)}</td>
                      <td className="p-3 font-body text-sm text-warm-gray">{p.vilarejoName}</td>
                      <td className="p-3 font-body text-sm text-warm-gray">{PRA_QUEM[p.handle] || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="font-body text-xs text-warm-gray mt-2">
              Valores "a partir de", por pessoa salvo indicação (ref. jul/2026) — variam por operador, temporada e
              condições do mar. Almoço e degustações nunca estão inclusos.
            </p>
          </section>

          {/* Cards por experiência */}
          <section className="mb-16" id="experiencias">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-6">Cada rota em detalhe</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {nauticos.map((p: any) => (
                <Link
                  key={p.handle}
                  to={localize(`/passeio/${p.handle}`)}
                  className="group block bg-white rounded-2xl border border-sand-200 overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md hover:border-teal-light/40 transition-all"
                >
                  <div className="relative h-44">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    <span className="absolute top-3 right-3 font-body text-xs font-semibold bg-white/95 text-teal-deep px-2.5 py-1 rounded-full">
                      {precoLabel(p)}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-xl text-warm-dark mb-1 group-hover:text-teal-deep transition-colors">{p.title}</h3>
                    <p className="font-body text-sm text-warm-gray leading-relaxed mb-3">{p.subtitle}</p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="font-body text-[11px] bg-sand-100 text-warm-dark px-2 py-0.5 rounded-full">⏱️ {p.duration}</span>
                      {p.groupSize?.max && (
                        <span className="font-body text-[11px] bg-sand-100 text-warm-dark px-2 py-0.5 rounded-full">👥 até {p.groupSize.max}</span>
                      )}
                      <span className="font-body text-[11px] text-teal-deep font-semibold ml-auto self-center">Ver detalhes →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Bloco: os golfinhos */}
          <section className="mb-16 bg-teal-light/5 border border-teal-light/20 rounded-2xl p-6 sm:p-8" id="golfinhos">
            <span className="font-body text-sm text-teal-deep font-semibold uppercase tracking-wider">A estrela do passeio</span>
            <h2 className="font-display text-3xl text-warm-dark mt-1 mb-3">🐬 Os botos-cinza da baía</h2>
            <div className="max-w-3xl space-y-3">
              <p className="font-body text-warm-gray leading-relaxed">
                Quem mora na Baía de Paranaguá é o <strong>boto-cinza</strong> (Sotalia guianensis), um golfinho
                de ~2 metros que usa o trecho entre a ilha e a Ilha das Peças como berçário. Ele está lá o ano
                inteiro — no verão, época de procriação, a atividade aumenta e a chance de avistar também.
              </p>
              <p className="font-body text-warm-gray leading-relaxed">
                Duas regras de ouro: <strong>avistar nunca é garantido</strong> (são animais livres, sem script) e
                a observação é feita à distância, sem perseguir os grupos — os barqueiros locais conhecem o
                comportamento dos botos e sabem onde desligar o motor e esperar. Quer saber mais sobre o lugar?
                Veja o guia da{' '}
                <Link to={localize('/ponto-turistico/baia-dos-golfinhos')} className="text-teal-deep font-medium hover:underline">
                  Baía dos Golfinhos
                </Link>.
              </p>
            </div>
          </section>

          {/* Informações práticas */}
          <section className="mb-16" id="praticas">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-6">Antes de embarcar</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: '⚓', t: 'Segurança', d: 'Embarcações credenciadas pela Marinha, colete obrigatório e seguro por passageiro. A palavra final sobre a rota é sempre do marinheiro.' },
                { icon: '🎒', t: 'O que levar', d: 'Protetor solar, chapéu, capa impermeável pro celular e dinheiro/Pix — o almoço na Ilha das Peças e as ostras da Ponta Oeste são à parte.' },
                { icon: '🌊', t: 'E se o mar estiver ruim?', d: 'O trecho de mar aberto é curto, mas com vento forte o roteiro é ajustado ou remarcado. Quem enjoa deve tomar remédio antes de embarcar.' },
                { icon: '🧒', t: 'Com crianças', d: 'Pode, com colete — e as pequenas costumam pagar menos. Alguns operadores têm idade mínima no compartilhado: confirme na reserva.' },
              ].map(c => (
                <div key={c.t} className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-sand-200">
                  <span className="text-2xl flex-shrink-0" aria-hidden="true">{c.icon}</span>
                  <div>
                    <h3 className="font-display text-lg text-warm-dark mb-1">{c.t}</h3>
                    <p className="font-body text-sm text-warm-gray leading-relaxed">{c.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16" id="faq">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-6">Perguntas frequentes</h2>
            <div className="space-y-3 max-w-3xl">
              {faq.map((f, i) => (
                <details key={i} className="group bg-white border border-sand-200 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand-50 list-none">
                    <span className="font-display text-base text-warm-dark pr-4">{f.q}</span>
                    <span className="text-teal-deep flex-shrink-0 transition-transform duration-200 group-open:rotate-180">▼</span>
                  </summary>
                  <div className="px-4 pb-4 font-body text-warm-gray text-sm leading-relaxed">{f.a}</div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA cross-sell */}
          <section className="mb-16 bg-teal-deep rounded-2xl p-8 lg:p-12 text-center">
            <h2 className="font-display text-3xl sm:text-4xl text-white mb-3">Complete o roteiro</h2>
            <p className="font-editorial text-white/80 mb-6 max-w-xl mx-auto">
              Resolva a travessia, escolha o que mais fazer e onde ficar depois do passeio.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to={localize('/como-chegar')} className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-sand-100 font-body font-semibold rounded-xl px-5 py-3 transition-all">Como chegar</Link>
              <Link to={localize('/o-que-fazer-ilha-do-mel')} className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-sand-100 font-body font-semibold rounded-xl px-5 py-3 transition-all">O que fazer</Link>
              <Link to={localize('/pousadas-ilha-do-mel')} className="inline-flex items-center gap-2 bg-white text-teal-deep hover:bg-sand-100 font-body font-semibold rounded-xl px-5 py-3 transition-all">Pousadas</Link>
            </div>
          </section>

          <AuthorBox author={defaultAuthor} />
        </Container>
      </div>
    </>
  );
}
